import { LeapDivisionFilter } from "@arrowsup/platform-dtos";
import { useInput } from "react-admin";
import { TextField as MuiTextField, Typography } from "@mui/material";
import { useState } from "react";
import * as ss from "superstruct";
import { IntegrationConnectionInput } from "../../../connected-accounts/integration-connection-input";

type DivisionIds = LeapDivisionFilter["includeDivisionIds"];

const DivisionIdList = ss.array(ss.number());

const formatDivisionIds = (ids: DivisionIds): string =>
  ids ? ids.join(", ") : "";

/** Always an array: an empty one clears a saved filter on edit. */
const parseDivisionIds = (text: string): number[] =>
  text
    .split(/[\s,]+/)
    .filter(Boolean)
    .map(Number);

const validateDivisionIds = (ids: DivisionIds): string | undefined =>
  Array.isArray(ids) && ids.some((id) => !Number.isInteger(id) || id < 1)
    ? "Enter whole-number division IDs, separated by commas."
    : undefined;

/**
 * Comma-separated ids.  Keeps its own text so a half-typed list ("12, ")
 * isn't reformatted under the cursor.
 */
const LeapDivisionIdsInput: React.FC<{
  source: string;
  label: string;
  helperText: string;
}> = ({ source, label, helperText }) => {
  const { field, fieldState } = useInput({
    source,
    validate: validateDivisionIds,
  });

  const [text, setText] = useState(() =>
    formatDivisionIds(
      ss.is(field.value, DivisionIdList) ? field.value : undefined
    )
  );

  return (
    <MuiTextField
      label={label}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        field.onChange(parseDivisionIds(e.target.value));
      }}
      onBlur={field.onBlur}
      error={!!fieldState.error}
      helperText={fieldState.error?.message ?? helperText}
      fullWidth
    />
  );
};

/**
 * Leap assigns divisions to jobs, not customers, so leads can't be filtered,
 * and Leap's appointment filter has no exclusion.
 */
export const LeapSourceConfig: React.FC<{
  divisions: "none" | "include" | "include-exclude";
}> = ({ divisions }) => {
  return (
    <>
      <IntegrationConnectionInput type="leap" />
      {divisions === "none" ? (
        <Typography variant="body2" color="text.secondary">
          Leads cannot be filtered by division: Leap assigns divisions to jobs,
          not customers. Use Leap Job Created to count leads by division.
        </Typography>
      ) : (
        <LeapDivisionIdsInput
          label="Only These Divisions"
          source="source.includeDivisionIds"
          helperText={
            divisions === "include"
              ? "Leap division IDs, comma-separated. Leave blank for all. Only appointments booked on a job in these divisions are ingested."
              : "Leap division IDs, comma-separated. Leave blank for all. Jobs with no division are skipped when this is set."
          }
        />
      )}
      {divisions === "include-exclude" && (
        <LeapDivisionIdsInput
          label="Exclude Divisions"
          source="source.excludeDivisionIds"
          helperText="Leap division IDs, comma-separated. Jobs in these divisions are skipped; jobs with no division are kept."
        />
      )}
    </>
  );
};
